/**
 * Portfolio details API — LOADS one saved portfolio into Manage Portfolio, when
 * it is picked in the sidebar (and again after Save / on Cancel).
 *
 * Legacy (Portfolios.vue getPortfolioInstances, apiservice.js getPortfolioInstances):
 *   GET /get-portfolio?portfolioName=<name>&user_email=<OWNER>&id=<_id>&is_billing_data=<bool>
 *     -> { Data: { input_data: [...], udf: [...], headroom, pricing_model_discount, policy_engine } }
 *
 * Same path as the sidebar LIST (dashboard/api/portfoliosApi.js), different
 * params and a different answer. `user_email` is the portfolio's owner, not the
 * signed-in user — an Org portfolio is filed under whoever made it.
 * `is_billing_data` is true for a portfolio opened in Billing (CUR) mode
 * (legacy Portfolios.vue :465, `selectedItem === 'Billing'`), false otherwise.
 *
 * A lazy query that ALWAYS reaches the server (no cache kept): opening the same
 * portfolio twice, or Cancel, must show what is saved now, not what was loaded
 * before. No tags either — Save reloads it explicitly.
 */
import { fromPricingModelDiscount } from '@components/common/ContractDiscountDialog/contractDiscountRules';
import { DEFAULT_HEADROOM } from '@components/eia/portfolioFormRules';
import { API_ENDPOINTS } from '@services/apiEndpoints';
import { envelopeError, isFailureEnvelope } from '@services/apiMessages';
import { baseApi } from '@services/baseApi';

const { PORTFOLIOS } = API_ENDPOINTS.MANAGE_PORTFOLIO;

function listOf(value) {
  return Array.isArray(value) ? value : [];
}

/**
 * The reply -> what the page shows, or THROW on a failure envelope / no Data.
 * `headroom` falls back to the default (20), as legacy's slider does on a
 * portfolio saved before headroom existed. `discount` is the saved
 * `pricing_model_discount` (2026-10-21, replaces `msa_discount`) as the UI
 * value { onDemand, oneYearRi, threeYearRi }, or null when none was saved.
 *
 * @returns {{ instances: object[], selfPerf: object[], headroom: number,
 *             discount: object | null, policyEngine: string | null }}
 */
export function readPortfolioDetails(response) {
  const data = response?.Data;
  if (isFailureEnvelope(response) || !data || typeof data !== 'object') {
    throw envelopeError(response, 'Could not load the portfolio.');
  }
  const headroom = Number(data.headroom);
  return {
    instances: listOf(data.input_data),
    selfPerf: listOf(data.udf),
    headroom: Number.isFinite(headroom) && data.headroom !== null ? headroom : DEFAULT_HEADROOM,
    discount: fromPricingModelDiscount(data.pricing_model_discount),
    policyEngine: data.policy_engine ?? null,
  };
}

/**
 * The /get-portfolio params for one portfolio.
 *  - `provider`: only the Telemetry view sends one — the connector's slug, as
 *    legacy's DynamicTelemetryForm.vue getPortfolioInstances (:797-811).
 *  - `billing`: the portfolio was opened in Billing (CUR) mode — the open
 *    portfolio's own `billing` flag (dashboard toOpenPortfolio), so Cancel and
 *    the reload after Save ask the same way as the first open.
 */
export function portfolioDetailsParams({ id, name, user, provider, billing = false }) {
  return {
    portfolioName: name,
    user_email: user,
    id,
    is_billing_data: Boolean(billing),
    ...(provider ? { provider } : {}),
  };
}

export const portfolioDetailsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPortfolioDetails: builder.query({
      query: (args) => ({ url: PORTFOLIOS, params: portfolioDetailsParams(args) }),
      keepUnusedDataFor: 0,
    }),
  }),
});

export const { useLazyGetPortfolioDetailsQuery } = portfolioDetailsApi;
